import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useCreateAdmin } from '@/api/partners/admins/use-create-admin';
import { FinishProfileModal } from '@/components/checkout/finish-profile-modal';
import { isDesktop, useAuth } from '@/core';
import metaPixel from '@/core/clients/meta';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Checkbox, Text } from '@/ui';

const mobileDarkImg = require('../../../public/images/partners/partner-opt-in-dark.png');
const mobileLightImg = require('../../../public/images/partners/partner-opt-in-light.png');
const desktopDarkImg = require('../../../public/images/partners/partner-opt-in-desktop-dark.png');
const desktopLightImg = require('../../../public/images/partners/partner-opt-in-desktop-light.png');

export default function JoinPartnerProgram() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const [acceptedConditions, setAcceptedConditions] = useState(false);
  const [showFinishProfileModal, setShowFinishProfileModal] = useState(false);
  const status = useAuth.use.status();
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);

  const {
    data: customer,
    isLoading: isGettingCustomer,
    isError: isGettingCustomerError,
    refetch: refetchCustomer,
  } = useCustomer({
    retry: false,
  });

  const { mutateAsync: createAdmin, isPending: isCreatingAdmin } =
    useCreateAdmin();

  const imageSrc = useMemo(() => {
    if (isDesktop()) {
      return UnistylesRuntime.themeName === 'dark'
        ? desktopDarkImg
        : desktopLightImg;
    }

    return UnistylesRuntime.themeName === 'dark'
      ? mobileDarkImg
      : mobileLightImg;
  }, []);

  useEffect(() => {
    metaPixel.track('Page Viewed', { 'Page Name': 'Join Partner Program' });
  }, []);

  useEffect(() => {
    if (status === 'signIn' && customer?.partnerToken) {
      setPartnerMode(true);
      router.replace('/partner/cars');
      return;
    }
  }, [status, customer]);

  const createOwnerAndEnter = async () => {
    try {
      await createAdmin({});
      await refetchCustomer();

      setPartnerMode(true);
      router.replace('/partner/cars');
    } catch {
      console.log('Error creating admin user');
    }
  };

  const handleSwitchToPartner = async () => {
    if (customer?.partnerToken) {
      setPartnerMode(true);
      router.replace('/partner/cars');
      return;
    }

    if (!customer?.setupComplete) {
      setShowFinishProfileModal(true);
      return;
    }

    await createOwnerAndEnter();
  };

  const handleBack = () => {
    // Most entries here are pushes (renter Account / navbar "Become a
    // partner"), so back returns to the origin. The owner-entry path arrives
    // via replace, so fall back to the public search page when there's no
    // history to pop.
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/search');
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.body}>
        <Image style={styles.image} source={imageSrc} contentFit="cover" />

        <View style={{ gap: 8 }}>
          <Text type="title1" style={{ textAlign: 'center' }}>
            Turn Your Car Into A Revenue Stream
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary, textAlign: 'center' }}
          >
            List your vehicle and earn when you're not driving. You can switch
            between customer and owner accounts anytime.
          </Text>
        </View>

        <Pressable
          onPress={async () => {
            const baseUrl = 'https://www.eonrides.com/partners';

            if (Platform.OS === 'web') {
              window.open(baseUrl, '_blank');
            } else {
              await WebBrowser.openBrowserAsync(baseUrl);
            }
          }}
        >
          <Text type="bodyRegular" style={{ textDecorationLine: 'underline' }}>
            Learn More About The Eon's Owner Program
          </Text>
        </Pressable>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Checkbox
            onPress={() => setAcceptedConditions(!acceptedConditions)}
            active={acceptedConditions}
          />

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Text type="bodyRegular">I agree to the</Text>

            <Pressable
              onPress={async () => {
                const baseUrl =
                  'https://eonrides.notion.site/Terms-of-Service-Important-Legal-Disclaimers-1bab67e0f90e803498e5e0982b1677ce';

                if (Platform.OS === 'web') {
                  window.open(baseUrl, '_blank');
                } else {
                  await WebBrowser.openBrowserAsync(baseUrl);
                }
              }}
            >
              <Text
                type="bodyRegular"
                style={{ textDecorationLine: 'underline' }}
              >
                Owner Terms of Service
              </Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.buttonDesktopContainer}>
          <Button
            text="Create Owner Account"
            type="primary"
            onPress={handleSwitchToPartner}
            disabled={!acceptedConditions || isCreatingAdmin}
          />
          <Button text="Go Back" type="ghost" onPress={handleBack} />
        </View>
      </View>

      <View style={styles.buttonMobileContainer}>
        <Button
          text="Create Owner Account"
          type="primary"
          onPress={handleSwitchToPartner}
          disabled={!acceptedConditions || isCreatingAdmin}
        />
        <Button text="Go Back" type="ghost" onPress={handleBack} />
      </View>

      <FinishProfileModal
        visible={showFinishProfileModal}
        onClose={() => setShowFinishProfileModal(false)}
        onDone={() => {
          setShowFinishProfileModal(false);
          createOwnerAndEnter();
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    backgroundColor: theme.colors.background_1,
    flex: 1,
  },
  body: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    gap: 24,
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
  },
  image: {
    width: '100%',
    height: 275,
  },
  buttonDesktopContainer: {
    gap: 8,
    display: isDesktop() ? 'flex' : 'none',
  },
  buttonMobileContainer: {
    padding: 16,
    paddingBottom: 32,
    gap: 8,
    display: isDesktop() ? 'none' : 'flex',
  },
}));
